import type { RefObject } from "react";

import { useLayoutEffect } from "react";

/**
 * While `enabled`, keeps the fixed `target` pinned to where the host page would place `anchor`, so it fills the
 * viewport below the host's header and follows it when the document scrolls. The host resizes its header after load
 * (jQuery swaps the desktop menu for a hamburger below 1000px), so the offset is re-read whenever the body resizes.
 */
export function usePinnedBelowHost(anchor: RefObject<HTMLElement>, target: RefObject<HTMLElement>, enabled: boolean) {
	useLayoutEffect(() => {
		const anchorElement = anchor.current;
		const targetElement = target.current;
		if (!enabled || !anchorElement || !targetElement) return;

		const update = () => {
			const top = `${Math.max(0, anchorElement.getBoundingClientRect().top)}px`;
			if (targetElement.style.top !== top) targetElement.style.top = top;
		};
		update();

		const observer = new ResizeObserver(update);
		observer.observe(document.body);
		window.addEventListener("resize", update);
		window.addEventListener("scroll", update, { passive: true });
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", update);
			window.removeEventListener("scroll", update);
			targetElement.style.removeProperty("top");
		};
	}, [anchor, target, enabled]);
}
